import { getSafeSheetName } from "./sheetUtils";

const parseNumeric = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const hasAnyContent = (cells) =>
  cells.some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return Number.isFinite(Number(value));
  });

// ═══ i18n dictionary for child (detailed) BOQ sheet labels ═══
const CHILD_TRANSLATIONS = {
  geo: {
    col_n: "N",
    col_desc: "სამუშაოების დასახელება",
    col_unit: "განზ. ერთ.",
    col_normative: "ნორმატიული",
    col_material: "მასალა",
    col_labour: "ხელფასი",
    col_equipment: "მექანიზმი/ტრანსპორტი",
    col_total: "სულ",
    col_code: "შიფრი",
    sub_unit: "ერთ.",
    sub_qty: "რაოდ",
    sub_sum: "ჯამი",
    sub_price: "ფასი",
    chapter_prefix: "1.0 თავი",
    sum: "ჯამი",
    overhead: "ზედნადები ხარჯები",
    accumulation: "გეგმიური დაგროვება",
  },
  eng: {
    col_n: "N",
    col_desc: "Description of works",
    col_unit: "Unit",
    col_normative: "Normative",
    col_material: "Material",
    col_labour: "Labour",
    col_equipment: "Equipment / Transport",
    col_total: "Total",
    col_code: "Code",
    sub_unit: "Unit",
    sub_qty: "Qty",
    sub_sum: "Sum",
    sub_price: "Price",
    chapter_prefix: "1.0 Chapter",
    sum: "Total",
    overhead: "Overhead",
    accumulation: "Accumulation",
  },
  rus: {
    col_n: "№",
    col_desc: "Наименование работ",
    col_unit: "Ед. изм.",
    col_normative: "Нормативный",
    col_material: "Материал",
    col_labour: "Зарплата",
    col_equipment: "Механизм / Транспорт",
    col_total: "Всего",
    col_code: "Шифр",
    sub_unit: "Ед.",
    sub_qty: "Кол.",
    sub_sum: "Итого",
    sub_price: "Цена",
    chapter_prefix: "1.0 Глава",
    sum: "Итого",
    overhead: "Накладные расходы",
    accumulation: "Плановые накопления",
  },
};

function pickLocale(lang) {
  const key = String(lang || "geo").toLowerCase();
  if (key === "eng" || key === "en" || key === "english") return "eng";
  if (key === "rus" || key === "ru" || key === "russian") return "rus";
  return "geo";
}

// Union of all summary / overhead / accumulation labels across languages — used when reading.
export const CHILD_SUMMARY_STOP_WORDS = new Set([
  ...Object.values(CHILD_TRANSLATIONS).map((t) => t.sum),
  ...Object.values(CHILD_TRANSLATIONS).map((t) => t.overhead),
  ...Object.values(CHILD_TRANSLATIONS).map((t) => t.accumulation),
]);

const CHILD_SUM_WORDS = new Set(
  Object.values(CHILD_TRANSLATIONS).map((t) => t.sum)
);

/**
 * Child BOQ sheet (დეტალური format).
 * Columns A–M: N, Desc, Unit, NormUnit, NormQty, MatPrice, MatTotal, LabPrice, LabTotal, EqPrice, EqTotal, Total, Code
 */
export async function writeCustomBoqTable(
  projectData,
  boqData,
  sheetTitle = "BOQ1",
  level = 1,
  chapterTitle = ""
) {
  const L = CHILD_TRANSLATIONS[pickLocale(projectData?.language)];
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheetName = getSafeSheetName(sheetTitle || "BOQ1");
    let sheet = workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) {
      sheet = workbook.worksheets.add(sheetName);
    } else {
      sheet.getUsedRange().clear(Excel.ClearApplyTo.all);
    }
    sheet.activate();
    await context.sync();

    const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

    // ── Page setup (A4 landscape, fit to 1 page wide) ──
    const pl = sheet.pageLayout;
    pl.orientation = "Landscape";
    pl.paperSize = "A4";
    pl.fitToPage = true;
    pl.fitToWidth = 1;
    pl.fitToHeight = 0;
    pl.topMargin = 0.5;
    pl.bottomMargin = 0.5;
    pl.leftMargin = 0.5;
    pl.rightMargin = 0.5;
    await context.sync();

    // ── Row heights ──
    sheet.getRange("1:1").format.rowHeight = 20;
    sheet.getRange("2:2").format.rowHeight = 20;
    sheet.getRange("3:3").format.rowHeight = 20;
    sheet.getRange("4:4").format.rowHeight = 18;
    sheet.getRange("5:5").format.rowHeight = 6;
    sheet.getRange("6:6").format.rowHeight = 20;
    sheet.getRange("7:7").format.rowHeight = 6;
    sheet.getRange("8:8").format.rowHeight = 6;
    await context.sync();

    // Column widths (pixels) — tuned for A4 landscape: A=N, B=Desc, C=Unit, D=NormUnit, E=NormQty, F=MatPrice, G=MatTotal, H=LabPrice, I=LabTotal, J=EqPrice, K=EqTotal, L=Total, M=Code
    const colWidths = [35, 215, 60, 52, 52, 68, 78, 68, 78, 68, 78, 88, 68];
    for (let i = 0; i < colWidths.length; i++) {
      sheet.getRangeByIndexes(0, i, 1, 1).format.columnWidth = colWidths[i];
    }
    await context.sync();

    // ── Row 1: Group headers ──
    sheet.getRange("A1:M1").values = [[
      L.col_n, L.col_desc, L.col_unit, L.col_normative, "",
      L.col_material, "", L.col_labour, "", L.col_equipment, "", L.col_total, L.col_code,
    ]];
    // Row 2: Sub-headers
    sheet.getRange("A2:M2").values = [[
      "", "", "", L.sub_unit, L.sub_qty, L.sub_unit, L.sub_sum, L.sub_unit, L.sub_sum, L.sub_unit, L.sub_sum, "", "",
    ]];
    // Row 3: Price labels
    sheet.getRange("A3:M3").values = [[
      "", "", "", "", "", L.sub_price, "", L.sub_price, "", L.sub_price, "", "", "",
    ]];
    await context.sync();

    // Merge header cells
    sheet.getRange("A1:A3").merge();
    sheet.getRange("B1:B3").merge();
    sheet.getRange("C1:C3").merge();
    sheet.getRange("D1:E1").merge();
    sheet.getRange("D2:D3").merge();
    sheet.getRange("E2:E3").merge();
    sheet.getRange("F1:G1").merge();
    sheet.getRange("G2:G3").merge();
    sheet.getRange("H1:I1").merge();
    sheet.getRange("I2:I3").merge();
    sheet.getRange("J1:K1").merge();
    sheet.getRange("K2:K3").merge();
    sheet.getRange("L1:L3").merge();
    sheet.getRange("M1:M3").merge();

    // Style headers
    const headerRange = sheet.getRange("A1:M3");
    headerRange.format.fill.color = "#00b3b3";
    headerRange.format.font.color = "white";
    headerRange.format.font.bold = true;
    headerRange.format.font.size = 10;
    headerRange.format.horizontalAlignment = "Center";
    headerRange.format.verticalAlignment = "Center";
    headerRange.format.wrapText = true;
    await context.sync();

    // Row 4: Column numbers
    sheet.getRange("A4:M4").values = [
      ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13"],
    ];
    sheet.getRange("A4:M4").format.fill.color = "#1f2933";
    sheet.getRange("A4:M4").format.font.color = "white";
    sheet.getRange("A4:M4").format.font.bold = true;
    sheet.getRange("A4:M4").format.horizontalAlignment = "Center";
    await context.sync();

    // ── Chapter header (row 6) ──
    const chapter = chapterTitle || (projectData?.chapterTitle || "");
    if (chapter) {
      sheet.getRange("A6:M6").values = [[L.chapter_prefix, chapter, "", "", "", "", "", "", "", "", "", "", ""]];
      sheet.getRange("A6:B6").format.font.bold = true;
      sheet.getRange("A6:B6").format.font.size = 12;
    }

    // ── Data rows (starting row 9) ──
    const DATA_START = 9;
    const dataRows = boqData && boqData.length ? boqData : [];
    const rowCount = Math.max(dataRows.length, 15);

    const values = [];
    for (let i = 0; i < rowCount; i++) {
      const row = dataRows[i] || {};
      const normQty = toNumber(row.norm_qty || row.Qty_Per_Work_Unit);
      const matPrice = toNumber(row.mat_price || (row.resource_type === "MATERIAL" ? row.Unit_Rate : 0));
      const matTotal = toNumber(row.mat_total) || (row.resource_type === "MATERIAL" ? normQty * matPrice : 0);
      const labPrice = toNumber(row.salary_price || (row.resource_type === "LABOR" ? row.Unit_Rate : 0));
      const labTotal = toNumber(row.salary_total) || (row.resource_type === "LABOR" ? normQty * labPrice : 0);
      const eqPrice = toNumber(row.transport_price || (row.resource_type === "EQUIPMENT" ? row.Unit_Rate : 0));
      const eqTotal = toNumber(row.transport_total) || (row.resource_type === "EQUIPMENT" ? normQty * eqPrice : 0);
      const rowTotal = toNumber(row.row_total) || (matTotal + labTotal + eqTotal);

      values.push([
        row.display_id || row.N || "",
        row.title || row.Material_Name_GEO || "",
        row.dimension || row.Base_Unit || "",
        row.norm_unit || "",
        normQty || "",
        matPrice || "",
        matTotal || "",
        labPrice || "",
        labTotal || "",
        eqPrice || "",
        eqTotal || "",
        rowTotal || "",
        row.code || row.work_code || "",
      ]);
    }

    sheet.getRangeByIndexes(DATA_START - 1, 12, values.length, 1).numberFormat = values.map(() => ["@"]);
    sheet.getRangeByIndexes(DATA_START - 1, 0, values.length, 13).values = values;
    await context.sync();

    // Replace total columns with live formulas so they recalculate when prices/qty change
    if (rowCount > 0) {
      const fG = [], fI = [], fK = [], fL = [];
      for (let i = 0; i < rowCount; i++) {
        const r = DATA_START + i;
        fG.push([`=IF(OR(F${r}="",E${r}=""),0,F${r}*E${r})`]);
        fI.push([`=IF(OR(H${r}="",E${r}=""),0,H${r}*E${r})`]);
        fK.push([`=IF(OR(J${r}="",E${r}=""),0,J${r}*E${r})`]);
        fL.push([`=SUM(G${r},I${r},K${r})`]);
      }
      sheet.getRangeByIndexes(DATA_START - 1, 6, rowCount, 1).formulas = fG;   // G MatTotal
      sheet.getRangeByIndexes(DATA_START - 1, 8, rowCount, 1).formulas = fI;   // I LabTotal
      sheet.getRangeByIndexes(DATA_START - 1, 10, rowCount, 1).formulas = fK;  // K EqTotal
      sheet.getRangeByIndexes(DATA_START - 1, 11, rowCount, 1).formulas = fL;  // L RowTotal
    }
    await context.sync();

    // Style data rows
    for (let i = 0; i < values.length; i++) {
      const r = DATA_START + i;
      const rowRange = sheet.getRange(`A${r}:M${r}`);
      rowRange.format.rowHeight = 18;
      rowRange.format.verticalAlignment = "Center";
      rowRange.format.font.size = 10;
      sheet.getRange(`F${r}:L${r}`).numberFormat = [["#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000", "#,##0.000"]];
      sheet.getRange(`D${r}:L${r}`).format.horizontalAlignment = "Right";

      // Bold for position headers (e.g., "1.1" vs "1.1.1")
      const id = String(values[i][0]);
      if (id && id.split(".").length <= 2) {
        rowRange.format.font.bold = true;
        rowRange.format.fill.color = "#E8F4F8";
      }
    }
    await context.sync();

    // ── Summary block ──
    const dataEnd = DATA_START + values.length - 1;
    const S = dataEnd + 2;

    const summaryLabels = [L.sum, L.overhead, L.sum, L.accumulation, L.sum];
    const summaryData = summaryLabels.map((label) => ["", label, "", "", "", "", "", "", "", "", "", "", ""]);
    sheet.getRangeByIndexes(S - 1, 0, 5, 13).values = summaryData;

    // Overhead and accumulation percentages (configurable via project cost config)
    const _cfg = projectData?.costConfig || {};
    const overheadRate     = (_cfg.overhead?.enabled     !== false ? (_cfg.overhead?.pct     ?? 10) : 0) / 100;
    const accumulationRate = (_cfg.accumulation?.enabled !== false ? (_cfg.accumulation?.pct ?? 8)  : 0) / 100;

    sheet.getRange(`E${S + 1}`).values = [[overheadRate]];
    sheet.getRange(`E${S + 1}`).numberFormat = [["0%"]];
    sheet.getRange(`E${S + 3}`).values = [[accumulationRate]];
    sheet.getRange(`E${S + 3}`).numberFormat = [["0%"]];
    await context.sync();

    // Formulas for G (Mat), I (Lab), K (Eq), L (Total)
    ["G", "I", "K", "L"].forEach((col) => {
      sheet.getRange(`${col}${S}`).formulas = [[`=SUM(${col}${DATA_START}:${col}${dataEnd})`]];
      sheet.getRange(`${col}${S + 1}`).formulas = [[`=${col}${S}*${overheadRate}`]];
      sheet.getRange(`${col}${S + 2}`).formulas = [[`=${col}${S}+${col}${S + 1}`]];
      sheet.getRange(`${col}${S + 3}`).formulas = [[`=${col}${S + 2}*${accumulationRate}`]];
      sheet.getRange(`${col}${S + 4}`).formulas = [[`=${col}${S + 2}+${col}${S + 3}`]];
    });

    // Style summary
    const summaryColors = ["#00b3b3", "#1f2933", "#00b3b3", "#1f2933", "#00b3b3"];
    for (let i = 0; i < 5; i++) {
      const r = S + i;
      const row = sheet.getRange(`A${r}:M${r}`);
      row.format.fill.color = summaryColors[i];
      row.format.font.color = "white";
      row.format.font.bold = true;
      row.format.verticalAlignment = "Center";
      sheet.getRange(`B${r}`).format.horizontalAlignment = "Right";
      sheet.getRange(`G${r}`).numberFormat = [["#,##0.000"]];
      sheet.getRange(`I${r}`).numberFormat = [["#,##0.000"]];
      sheet.getRange(`K${r}`).numberFormat = [["#,##0.000"]];
      sheet.getRange(`L${r}`).numberFormat = [["#,##0.000"]];
    }

    await context.sync();
  });
}

/**
 * Reads data from a detailed child BOQ sheet.
 */
export async function readCustomBoqTable(sheetTitle = "BOQ1") {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheetName = getSafeSheetName(sheetTitle || "BOQ1");
    const sheet = workbook.worksheets.getItemOrNullObject(sheetName);
    sheet.load("name");
    await context.sync();

    if (sheet.isNullObject) return [];

    const usedRange = sheet.getUsedRangeOrNullObject();
    usedRange.load(["values", "rowCount", "columnCount"]);
    await context.sync();

    if (usedRange.isNullObject || !usedRange.values || !usedRange.values.length) return [];

    const values = usedRange.values;
    const parsedRows = [];

    // Data starts at row 9 (index 8) in new format, or row 3 (index 2) in old format
    const startIdx = values.length > 8 && String(values[3]?.[0] || "").trim() === "1" ? 8 : 2;

    for (let idx = startIdx; idx < values.length; idx++) {
      const row = values[idx] || [];
      if (!hasAnyContent(row)) continue;

      const label = String(row[1] || "").trim();
      if (CHILD_SUMMARY_STOP_WORDS.has(label)) break;

      const displayId = String(row[0] || "").trim();
      const title = String(row[1] || "").trim();
      const unit = String(row[2] || "").trim();
      const normQty = parseNumeric(row[4]);
      const matPrice = parseNumeric(row[5]);
      const matTotal = parseNumeric(row[6]);
      const labPrice = parseNumeric(row[7]);
      const labTotal = parseNumeric(row[8]);
      const equipPrice = parseNumeric(row[9]);
      const equipTotal = parseNumeric(row[10]);
      const code = String(row[12] || "").trim();

      if (!title && !unit && matTotal === 0 && labTotal === 0 && equipTotal === 0) continue;

      let resourceType = "";
      if (labTotal > 0 || labPrice > 0) resourceType = "LABOR";
      else if (equipTotal > 0 || equipPrice > 0) resourceType = "EQUIPMENT";
      else if (matTotal > 0 || matPrice > 0) resourceType = "MATERIAL";

      // Unit_Rate must be the PER-UNIT price for this resource type, NOT the
      // row total. Everything downstream (server _compute_table_breakdown,
      // Load -> Excel, analytics getRowTotal) multiplies unit_rate x qty
      // again, so storing the total here re-multiplies by the quantity on
      // every save/load cycle — the runaway-inflation bug.
      const unitPrice =
        resourceType === "LABOR" ? labPrice :
        resourceType === "EQUIPMENT" ? equipPrice :
        resourceType === "MATERIAL" ? matPrice :
        (matPrice || labPrice || equipPrice || 0);

      parsedRows.push({
        Display_ID: displayId || String(parsedRows.length + 1),
        Material_Name_GEO: title,
        Base_Unit: unit,
        Unit_Rate: unitPrice,
        Currency: "GEL",
        Discipline: resourceType || "BOQ Child",
        Resource_Type: resourceType,
        Qty_Per_Work_Unit: normQty,
        Work_Code: code,
        Child_Table_Id: null,
      });
    }

    return parsedRows;
  });
}
